import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import { analyticsService } from '../../services/analyticsService';
import { auditService } from '../../services/auditService';
import { Users, ShoppingBag, Activity, TrendingUp, AlertCircle, Clock, Bell, X, AlertTriangle, Info, CheckCircle2, Tag, MessageSquare, Wifi, Archive } from 'lucide-react';
import { realtimeMonitoringService } from '../../services/realtimeMonitoringService';
import { offerService } from '../../services/offerService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';



const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return num?.toLocaleString('sr-RS');
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0 RSD';
  return `${Number(amount)?.toLocaleString('sr-RS')} RSD`;
};

const StatCard = ({ title, value, icon: IconComponent, color, link, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    yellow: 'bg-yellow-50 text-yellow-600'
  };

  const content = (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-600">{title}</h3>
        <div className={`p-2 rounded-lg ${colorClasses?.[color] || colorClasses?.blue}`}>
          <IconComponent className="w-5 h-5" />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
  );

  return link ? <Link to={link}>{content}</Link> : content;
};

const AdminHub = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAlerts, setShowAlerts] = useState(false);
  const [alertStats, setAlertStats] = useState({ total: 0, unread: 0, critical: 0, urgent: 0 });

  // Offers state - lazy load on demand
  const [allOffers, setAllOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [offersFilter, setOffersFilter] = useState('all');
  const [offersVisible, setOffersVisible] = useState(false);

  // Negotiations state - lazy load on demand
  const [allNegotiations, setAllNegotiations] = useState([]);
  const [negotiationsLoading, setNegotiationsLoading] = useState(false);
  const [negotiationsFilter, setNegotiationsFilter] = useState('all');
  const [negotiationsVisible, setNegotiationsVisible] = useState(false);

  // Extra stat card counts
  const [activeListingsCount, setActiveListingsCount] = useState(0);
  const [onlineUsersCount, setOnlineUsersCount] = useState(0);

  const { user, profile } = useAuth();

  useEffect(() => {
    fetchDashboardData();
    loadAlerts();
    loadAlertStats();
    fetchActiveListingsCount();
    fetchOnlineUsersCount();
  }, []);

  const fetchActiveListingsCount = async () => {
    try {
      const { count } = await supabase
        ?.from('marketplace_wishes')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('is_active', true);
      setActiveListingsCount(count || 0);
    } catch (err) {
      console.error('Error fetching active listings count:', err);
    }
  };

  const fetchOnlineUsersCount = async () => {
    try {
      const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000)?.toISOString();
      const { count } = await supabase
        ?.from('user_profiles')
        ?.select('*', { count: 'exact', head: true })
        ?.gte('last_seen', fifteenMinutesAgo)
        ?.not('email', 'ilike', '%test%')
        ?.not('email', 'ilike', '%dummy%');
      setOnlineUsersCount(count || 0);
    } catch (err) {
      // Fallback: users active in last 5 min via updated_at
      try {
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000)?.toISOString();
        const { count } = await supabase
          ?.from('user_profiles')
          ?.select('*', { count: 'exact', head: true })
          ?.gte('updated_at', fiveMinutesAgo)
          ?.not('email', 'ilike', '%test%');
        setOnlineUsersCount(count || 0);
      } catch (e) {
        setOnlineUsersCount(0);
      }
    }
  };

  const loadAlerts = async () => {
    try {
      const [alertsData, count] = await Promise.all([
        realtimeMonitoringService?.getRecentAlerts(20),
        realtimeMonitoringService?.getUnreadCount()
      ]);
      setAlerts(alertsData);
      setUnreadCount(count);
    } catch (err) {
      console.error('Error loading alerts:', err);
    }
  };

  const loadAlertStats = async () => {
    try {
      const stats = await realtimeMonitoringService?.getAlertStats();
      setAlertStats(stats);
    } catch (err) {
      console.error('Error loading alert stats:', err);
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await realtimeMonitoringService?.markAsRead(alertId);
      setAlerts((prev) =>
        prev?.map((alert) =>
          alert?.id === alertId ? { ...alert, is_read: true } : alert
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking alert as read:', err);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await realtimeMonitoringService?.dismissAlert(alertId);
      setAlerts((prev) => prev?.filter((alert) => alert?.id !== alertId));
      const alert = alerts?.find((a) => a?.id === alertId);
      if (alert && !alert?.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await realtimeMonitoringService?.markAllAsRead();
      setAlerts((prev) => prev?.map((alert) => ({ ...alert, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': case 'urgent': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'warning': return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'info': return <Info className="w-5 h-5 text-blue-600" />;
      default: return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    }
  };

  const AlertPanel = () => (
    <div className="absolute right-0 top-16 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[600px] overflow-hidden z-50">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100">
        <div>
          <h3 className="font-bold text-gray-900">Obaveštenja sistema</h3>
          <p className="text-xs text-gray-600">{unreadCount} nepročitanih</p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button onClick={handleMarkAllAsRead} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
              Označi sve
            </button>
          )}
          <button onClick={() => setShowAlerts(false)} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>
      <div className="overflow-y-auto max-h-[500px]">
        {alerts?.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {alerts?.map((alert) => (
              <div key={alert?.id} className={`p-4 hover:bg-gray-50 transition-colors ${!alert?.is_read ? 'bg-blue-50' : ''}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="font-semibold text-sm text-gray-900 truncate">{alert?.title}</h4>
                  {!alert?.is_read && <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>}
                </div>
                <p className="text-xs text-gray-600 mb-2 line-clamp-2">{alert?.message}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(alert?.created_at)}
                  </span>
                  <div className="flex items-center gap-2">
                    {!alert?.is_read && (
                      <button onClick={() => handleMarkAsRead(alert?.id)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                        Pročitano
                      </button>
                    )}
                    <button onClick={() => handleDismissAlert(alert?.id)} className="text-xs text-gray-500 hover:text-gray-700">
                      Odbaci
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Nema obaveštenja</p>
          </div>
        )}
      </div>
    </div>
  );

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [metricsData, activityData] = await Promise.all([
        analyticsService?.getOverviewMetrics(),
        auditService?.getRecentActivity(5)
      ]);
      setMetrics(metricsData);
      setRecentActivity(activityData);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllOffers = async () => {
    setOffersLoading(true);
    try {
      const data = await offerService?.getAllOffersAdmin();
      setAllOffers(data || []);
    } catch (err) {
      console.error('Error fetching all offers:', err);
    } finally {
      setOffersLoading(false);
    }
  };

  const handleShowOffers = () => {
    setOffersVisible(true);
    if (allOffers?.length === 0) fetchAllOffers();
  };

  const fetchAllNegotiations = async () => {
    setNegotiationsLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('negotiations')
        ?.select(`
          id,
          status,
          initial_message,
          created_at,
          updated_at,
          buyer_id,
          seller_id,
          initiator_id,
          responder_id,
          offer_id,
          buyer:user_profiles!buyer_id(id, full_name, email),
          seller:user_profiles!seller_id(id, full_name, email),
          offer:marketplace_wishes!offer_id(id, wish_title, voucher_name)
        `)
        ?.order('updated_at', { ascending: false });
      if (error) throw error;
      setAllNegotiations(data || []);
    } catch (err) {
      console.error('Error fetching negotiations:', err);
    } finally {
      setNegotiationsLoading(false);
    }
  };

  const handleShowNegotiations = () => {
    setNegotiationsVisible(true);
    if (allNegotiations?.length === 0) fetchAllNegotiations();
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleString('sr-RS', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Negotiation status config
  const NEG_STATUS_CONFIG = {
    initiated:   { label: 'Iniciran',   bg: 'bg-blue-100',   text: 'text-blue-700' },
    in_progress: { label: 'U toku',     bg: 'bg-indigo-100', text: 'text-indigo-700' },
    agreed:      { label: 'Dogovoreno', bg: 'bg-teal-100',   text: 'text-teal-700' },
    completed:   { label: 'Zaključen',  bg: 'bg-green-100',  text: 'text-green-700' },
    cancelled:   { label: 'Otkazan',    bg: 'bg-gray-100',   text: 'text-gray-600' },
  };

  const getNegotiationsByTab = (tab) => {
    if (tab === 'all') return allNegotiations;
    if (tab === 'active') return allNegotiations?.filter(n => n?.status === 'initiated' || n?.status === 'in_progress');
    if (tab === 'archived') return allNegotiations?.filter(n => n?.status === 'cancelled');
    if (tab === 'closed') return allNegotiations?.filter(n => n?.status === 'completed' || n?.status === 'agreed');
    return allNegotiations;
  };

  const negCounts = {
    all: allNegotiations?.length,
    active: allNegotiations?.filter(n => n?.status === 'initiated' || n?.status === 'in_progress')?.length,
    archived: allNegotiations?.filter(n => n?.status === 'cancelled')?.length,
    closed: allNegotiations?.filter(n => n?.status === 'completed' || n?.status === 'agreed')?.length,
  };

  const filteredNegotiations = getNegotiationsByTab(negotiationsFilter);

  const OFFER_STATUS_CONFIG = {
    active:   { label: 'Aktivan',   bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
    archived: { label: 'Arhiviran', bg: 'bg-gray-100',  text: 'text-gray-600', border: 'border-gray-200' },
    expired:  { label: 'Istekao',   bg: 'bg-red-100',   text: 'text-red-600',  border: 'border-red-200' },
  };

  const filteredOffers = allOffers?.filter(o => {
    if (offersFilter === 'all') return true;
    return o?.offerStatus === offersFilter;
  });

  const offerCounts = {
    all: allOffers?.length,
    active: allOffers?.filter(o => o?.offerStatus === 'active')?.length,
    archived: allOffers?.filter(o => o?.offerStatus === 'archived')?.length,
    expired: allOffers?.filter(o => o?.offerStatus === 'expired')?.length,
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <AdminSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">Učitavanje Admin Hub-a...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        <div className="p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Panel</h1>
                <p className="text-gray-600 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Centralizovani pregled sistema i ključnih metrika
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <button
                    onClick={() => setShowAlerts(!showAlerts)}
                    className="relative p-3 bg-white rounded-lg shadow-md hover:shadow-lg transition-all border border-gray-200"
                  >
                    <Bell className="w-6 h-6 text-gray-700" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {showAlerts && <AlertPanel />}
                </div>
                <button
                  onClick={fetchDashboardData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Osveži podatke
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6 flex items-start shadow-md">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-red-800 font-semibold">Greška pri učitavanju</h3>
                <p className="text-red-600 text-sm mt-1">{error}</p>
                <button onClick={fetchDashboardData} className="text-sm text-red-700 font-medium hover:text-red-800 mt-2 underline">
                  Pokušaj ponovo
                </button>
              </div>
            </div>
          )}

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Ukupno korisnika"
              value={formatNumber(metrics?.totalUsers)}
              icon={Users}
              color="blue"
              link="/admin-user-management-dashboard"
              subtitle="Registrovani korisnici"
            />
            <StatCard
              title="Novi korisnici"
              value={formatNumber(metrics?.newUsersLastMonth)}
              icon={TrendingUp}
              color="green"
              subtitle="Poslednjih 30 dana"
            />
            <StatCard
              title="Aktivni oglasi"
              value={formatNumber(activeListingsCount)}
              icon={ShoppingBag}
              color="purple"
              subtitle="Oglasi na oglasnoj tabli"
            />
            <StatCard
              title="Aktivni korisnici"
              value={formatNumber(onlineUsersCount)}
              icon={Wifi}
              color="yellow"
              subtitle="Online u poslednjih 15 min"
            />
          </div>

          {/* Oglasi na tržištu - load on demand */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Archive className="w-6 h-6 text-teal-600" />
                Oglasi na tržištu
              </h2>
              <div className="flex items-center gap-2">
                {offersVisible && (
                  <button onClick={fetchAllOffers} className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1">
                    <Activity className="w-4 h-4" />
                    Osveži
                  </button>
                )}
                {!offersVisible ? (
                  <button onClick={handleShowOffers} className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-semibold">
                    Prikaži
                  </button>
                ) : (
                  <button onClick={() => setOffersVisible(false)} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">
                    Sakrij
                  </button>
                )}
              </div>
            </div>

            {offersVisible && (
              <>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {[['all', 'Svi'], ['active', 'Aktivni'], ['archived', 'Arhivirani'], ['expired', 'Istekli']]?.map(([val, label]) => (
                    <button key={val} onClick={() => setOffersFilter(val)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                        offersFilter === val ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-gray-600 border-gray-200 hover:border-teal-400'
                      }`}>
                      {label} <span className="ml-1 opacity-70">({offerCounts?.[val] || 0})</span>
                    </button>
                  ))}
                </div>
                {offersLoading ? (
                  <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Učitavanje oglasa...</p>
                  </div>
                ) : filteredOffers?.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
                    <Archive className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">Nema oglasa u ovoj kategoriji</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Vaučer</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Tip</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Cena</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Ističe</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Objavljeno</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {filteredOffers?.map(offer => {
                            const statusConf = OFFER_STATUS_CONFIG?.[offer?.offerStatus] || OFFER_STATUS_CONFIG?.active;
                            return (
                              <tr key={offer?.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3">
                                  <div className="font-medium text-gray-900">{offer?.voucherName || '—'}</div>
                                  {offer?.voucherSymbol && (
                                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                                      <Tag size={10} />
                                      {offer?.voucherSymbol}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-gray-600 capitalize">{offer?.wishType || '—'}</td>
                                <td className="px-4 py-3 font-medium text-teal-700">
                                  {offer?.maxPrice ? `${Number(offer?.maxPrice)?.toLocaleString('sr-RS')} ${offer?.currency || 'RSD'}` : 'Po dogovoru'}
                                </td>
                                <td className="px-4 py-3 text-gray-500">
                                  {offer?.expiryDate ? new Date(offer?.expiryDate)?.toLocaleDateString('sr-RS') : '—'}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusConf?.bg} ${statusConf?.text} ${statusConf?.border}`}>
                                    {statusConf?.label}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-gray-400 text-xs">
                                  {offer?.createdAt ? new Date(offer?.createdAt)?.toLocaleDateString('sr-RS') : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pregovori u toku - load on demand */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-indigo-600" />
                Pregovori u toku
              </h2>
              <div className="flex items-center gap-2">
                {negotiationsVisible && (
                  <button onClick={fetchAllNegotiations} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                    <Activity className="w-4 h-4" />
                    Osveži
                  </button>
                )}
                {!negotiationsVisible ? (
                  <button onClick={handleShowNegotiations} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-semibold">
                    Prikaži
                  </button>
                ) : (
                  <button onClick={() => setNegotiationsVisible(false)} className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">
                    Sakrij
                  </button>
                )}
              </div>
            </div>

            {negotiationsVisible && (
              <>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {[
                    ['all', 'Svi'],
                    ['active', 'Aktivni'],
                    ['archived', 'Arhivirani'],
                    ['closed', 'Zaključeni'],
                  ]?.map(([val, label]) => (
                    <button key={val} onClick={() => setNegotiationsFilter(val)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                        negotiationsFilter === val
                          ? 'bg-indigo-700 text-white border-indigo-700' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-400'
                      }`}>
                      {label} <span className="ml-1 opacity-70">({negCounts?.[val] || 0})</span>
                    </button>
                  ))}
                </div>
                {negotiationsLoading ? (
                  <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Učitavanje pregovora...</p>
                  </div>
                ) : filteredNegotiations?.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
                    <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">Nema pregovora u ovoj kategoriji</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">ID</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Kupac</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Prodavac</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Oglas</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-600">Poslednja aktivnost</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {filteredNegotiations?.map(neg => {
                            const statusConf = NEG_STATUS_CONFIG?.[neg?.status] || { label: neg?.status, bg: 'bg-gray-100', text: 'text-gray-600' };
                            return (
                              <tr key={neg?.id} className="hover:bg-indigo-50 transition-colors">
                                <td className="px-4 py-3 font-mono text-xs text-gray-500">{neg?.id?.slice(0, 8)}...</td>
                                <td className="px-4 py-3">
                                  <div className="font-medium text-gray-900">{neg?.buyer?.full_name || '—'}</div>
                                  <div className="text-xs text-gray-400">{neg?.buyer?.email || ''}</div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="font-medium text-gray-900">{neg?.seller?.full_name || '—'}</div>
                                  <div className="text-xs text-gray-400">{neg?.seller?.email || ''}</div>
                                </td>
                                <td className="px-4 py-3 text-gray-700 text-xs max-w-[160px] truncate">
                                  {neg?.offer?.wish_title || neg?.offer?.voucher_name || '—'}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusConf?.bg} ${statusConf?.text}`}>
                                    {statusConf?.label}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-gray-400 text-xs">
                                  {neg?.updated_at ? new Date(neg?.updated_at)?.toLocaleDateString('sr-RS') : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHub;